import { useState, useEffect, useRef } from "react";
import FooterView from "../../components/footerView";

// ─── Font note ────────────────────────────────────────────────────────────────
// Add to index.html <head>:
// <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap" rel="stylesheet">
// tailwind.config.js theme.extend.fontFamily:
// serif: ['"Instrument Serif"', 'Georgia', 'serif'],
// sans:  ['"DM Sans"', 'sans-serif'],
// ─────────────────────────────────────────────────────────────────────────────

// ─── DATA ─────────────────────────────────────────────────────────────────────
const CASE_STUDIES = [
    {
        slug: "bloom-botanicals",
        client: "Bloom Botanicals",
        industry: "E-commerce / Skincare",
        location: "Atlanta, GA",
        href: "/case-studies/bloom-botanicals",
        services: ["On-Page SEO", "Technical Audit", "Content Writing"],
        category: "ecommerce",
        duration: "3 months",
        accent: "amber",
        featured: true,
        challenge:
            "A women-owned skincare brand with beautiful products but zero organic presence. Ranking on page 4+ for all core terms, losing sales to larger competitors with bigger ad budgets.",
        results: [
            { metric: "Organic traffic", before: "420/mo", after: "3,100/mo", delta: "+638%" },
            { metric: "Page 1 keywords", before: "2", after: "34", delta: "+1,600%" },
            { metric: "Revenue from organic", before: "$1,200/mo", after: "$8,400/mo", delta: "+600%" },
        ],
        quote:
            "We went from invisible to our best sales month ever — all from organic search. HerStory didn't just fix our SEO, they changed how we think about content.",
        quoteName: "Maya R., Founder",
        timeToResults: "6 weeks",
        image: "botanicals",
    },
    {
        slug: "crestview-law",
        client: "Crestview Family Law",
        industry: "Legal Services",
        location: "Austin, TX",
        href: "/case-studies/crestview-law",
        services: ["Local SEO", "Google Business Profile", "Technical Audit"],
        category: "local",
        duration: "2 months",
        accent: "violet",
        featured: false,
        challenge:
            "A family law practice with strong word-of-mouth referrals but no local search presence. Competitors dominated the Google Maps 3-pack, leaving them off the map entirely.",
        results: [
            { metric: "Google Maps ranking", before: "Not listed", after: "Top 3", delta: "3-pack" },
            { metric: "Monthly calls from Google", before: "4/mo", after: "41/mo", delta: "+925%" },
            { metric: "Profile views", before: "180/mo", after: "2,200/mo", delta: "+1,122%" },
        ],
        quote:
            "Within 6 weeks we were in the Google Maps 3-pack for our main keyword. We had to hire a second intake coordinator to handle the new leads.",
        quoteName: "James C., Managing Partner",
        timeToResults: "6 weeks",
        image: "law",
    },
    {
        slug: "her-table",
        client: "Her Table Co.",
        industry: "Food & Hospitality",
        location: "Brooklyn, NY",
        href: "/case-studies/her-table",
        services: ["GEO / AI SEO", "Content Writing", "Schema Markup"],
        category: "emerging",
        duration: "4 months",
        accent: "teal",
        featured: false,
        challenge:
            "A women-led supper club brand generating buzz on Instagram but barely discoverable on search or AI platforms. ChatGPT and Perplexity had no idea they existed.",
        results: [
            { metric: "AI platform citations", before: "0", after: "12+", delta: "New channel" },
            { metric: "Branded search volume", before: "80/mo", after: "940/mo", delta: "+1,075%" },
            { metric: "Organic reservations", before: "3/mo", after: "28/mo", delta: "+833%" },
        ],
        quote:
            "Someone told me they found us because ChatGPT recommended us when they asked for 'intimate dining experiences in Brooklyn.' That felt like the future.",
        quoteName: "Serena T., Co-Founder",
        timeToResults: "8 weeks",
        image: "food",
    },
    {
        slug: "steadfast-studio",
        client: "Steadfast Fitness Studio",
        industry: "Health & Wellness",
        location: "Denver, CO",
        href: "/case-studies/steadfast-studio",
        services: ["Local SEO", "Content Writing", "Link Building"],
        category: "local",
        duration: "5 months",
        accent: "amber",
        featured: false,
        challenge:
            "A boutique fitness studio competing against large chains and Classpass aggregators. They had loyal members but couldn't attract new ones through search.",
        results: [
            { metric: "Organic new members/mo", before: "2", after: "19", delta: "+850%" },
            { metric: "Local keyword rankings", before: "Pg. 3–5", after: "Pg. 1", delta: "Top 5" },
            { metric: "Website sessions", before: "310/mo", after: "2,650/mo", delta: "+755%" },
        ],
        quote:
            "We used to rely entirely on referrals. Now organic search is our #1 acquisition channel. I wish we'd done this two years ago.",
        quoteName: "Dana L., Owner",
        timeToResults: "10 weeks",
        image: "fitness",
    },
];

const CATEGORIES = [
    { id: "all", label: "All" },
    { id: "ecommerce", label: "E-commerce" },
    { id: "local", label: "Local Business" },
    { id: "emerging", label: "AI & Emerging" },
];

const ACCENT = {
    amber: {
        tag: "bg-amber-50 text-amber-800 border-amber-100",
        stat: "text-amber-700",
        border: "group-hover:border-amber-200",
        glow: "from-amber-50",
        badge: "bg-amber-100 text-amber-900",
        quote: "border-amber-300",
    },
    violet: {
        tag: "bg-violet-50 text-violet-800 border-violet-100",
        stat: "text-violet-700",
        border: "group-hover:border-violet-200",
        glow: "from-amber-50",
        badge: "bg-violet-100 text-violet-900",
        quote: "border-violet-300",
    },
    teal: {
        tag: "bg-teal-50 text-teal-800 border-teal-100",
        stat: "text-teal-700",
        border: "group-hover:border-teal-200",
        glow: "from-teal-50",
        badge: "bg-teal-100 text-teal-900",
        quote: "border-teal-300",
    },
};

// ─── Placeholder image backgrounds ───────────────────────────────────────────
const IMAGE_BG = {
    botanicals: "bg-gradient-to-br from-green-50 via-emerald-50 to-amber-50",
    law: "bg-gradient-to-br from-slate-100 via-stone-50 to-violet-50",
    food: "bg-gradient-to-br from-rose-50 via-amber-50 to-orange-50",
    fitness: "bg-gradient-to-br from-sky-50 via-teal-50 to-emerald-50",
};

const IMAGE_ICON = {
    botanicals: "🌿",
    law: "⚖️",
    food: "🍽️",
    fitness: "💪",
};

// ─── HOOKS ────────────────────────────────────────────────────────────────────
function useInView(threshold = 0.1) {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } },
            { threshold }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);
    return [ref, inView];
}

function Reveal({ children, delay = 0, className = "" }) {
    const [ref, inView] = useInView();
    return (
        <div
            ref={ref}
            className={className}
            style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(22px)",
                transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
            }}
        >
            {children}
        </div>
    );
}

// ─── NAV ──────────────────────────────────────────────────────────────────────
function Nav() {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const fn = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", fn);
        return () => window.removeEventListener("scroll", fn);
    }, []);
    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#faf9f6]/95 backdrop-blur-md border-b border-black/8 shadow-sm" : "bg-transparent"}`}>
            <div className="max-w-6xl mx-auto px-6 flex items-center justify-between py-4">
                <a href="/" className="font-serif text-lg text-stone-900 no-underline">
                    Her<span className="text-amber-700">Story</span> SEO
                </a>
                <div className="hidden md:flex items-center gap-4 text-xs text-stone-500">
                    <a href="/services" className="hover:text-stone-900 transition-colors no-underline">Services</a>
                    <a href="/case-studies" className="text-stone-900 font-medium no-underline">Case Studies</a>
                    <a href="/pricing" className="hover:text-stone-900 transition-colors no-underline">Pricing</a>
                </div>
                <a href="/pricing" className="text-xs font-medium bg-stone-900 text-white px-4 py-2 rounded-full no-underline hover:bg-amber-800 transition-colors duration-200">
                    Work with us
                </a>
            </div>
        </nav>
    );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────
function HeroSection() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);
    const fade = (d) => ({
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.85s ease ${d}ms, transform 0.85s ease ${d}ms`,
    });

    return (
        <section className="relative pt-36 pb-20 px-6 overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[400px] bg-violet-100/25 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-100/30 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            </div>

            <div className="relative max-w-6xl mx-auto">
                <div style={fade(80)} className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-stone-400 mb-6">
                    <span className="w-4 h-px bg-stone-300" />
                    Real Results
                    <span className="w-4 h-px bg-stone-300" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-end">
                    <div>
                        <h1 style={fade(180)} className="font-serif text-[clamp(42px,6vw,78px)] leading-[1.05] text-stone-900 mb-6">
                            What happens when<br />
                            <em className="italic text-amber-800">SEO actually works.</em>
                        </h1>
                        <p style={fade(300)} className="text-base text-stone-500 leading-relaxed font-light max-w-lg">
                            No vanity metrics. No inflated claims. These are real businesses — their real challenges, the exact work we did, and the numbers that followed.
                        </p>
                    </div>

                    {/* Aggregate stats */}
                    <div style={fade(380)}>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { n: "4", label: "Clients featured" },
                                { n: "6–10 wks", label: "Avg. time to first results" },
                                { n: "700%+", label: "Avg. traffic increase" },
                                { n: "100%", label: "Clients who renewed" },
                            ].map(({ n, label }) => (
                                <div key={label} className="bg-white rounded-2xl border border-black/8 px-5 py-4">
                                    <div className="font-serif text-2xl text-stone-800 mb-0.5">{n}</div>
                                    <div className="text-xs text-stone-400 font-light">{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ─── FILTER BAR ───────────────────────────────────────────────────────────────
function FilterBar({ active, onChange }) {
    return (
        <div className="sticky top-[65px] z-40 bg-[#faf9f6]/95 backdrop-blur-sm border-b border-black/5 py-3 px-6 mb-12">
            <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto">
                {CATEGORIES.map((cat) => (
                    <button
                        key={cat.id}
                        onClick={() => onChange(cat.id)}
                        className={`shrink-0 text-xs px-4 py-1.5 rounded-full border transition-all duration-200 ${
                            active === cat.id
                                ? "bg-stone-900 text-white border-stone-900"
                                : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                        }`}
                    >
                        {cat.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── FEATURED CARD (large, first) ─────────────────────────────────────────────
function FeaturedCard({ cs }) {
    const a = ACCENT[cs.accent];
    return (
        <Reveal>
            <a
                href={cs.href}
                className={`group relative grid grid-cols-1 lg:grid-cols-2 bg-white rounded-3xl border border-black/8 overflow-hidden no-underline transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${a.border} mb-6`}
            >
                {/* Image panel */}
                <div className={`relative ${IMAGE_BG[cs.image]} flex items-center justify-center min-h-[280px] lg:min-h-[420px]`}>
                    <div className="text-7xl">{IMAGE_ICON[cs.image]}</div>
                    <div className="absolute top-4 left-4">
            <span className={`text-[10px] font-medium tracking-widest uppercase px-2.5 py-1 rounded-full ${a.badge}`}>
              Featured
            </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex flex-wrap gap-1.5">
                            {cs.services.map((s) => (
                                <span key={s} className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${a.tag}`}>{s}</span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content panel */}
                <div className="p-8 lg:p-10 flex flex-col justify-between">
                    <div>
                        <div className="flex items-start justify-between mb-1">
                            <p className="text-xs text-stone-400 font-light">{cs.industry} · {cs.location}</p>
                            <div className="w-7 h-7 rounded-full border border-black/10 flex items-center justify-center transition-all duration-200 group-hover:border-black/30 group-hover:bg-stone-50 shrink-0">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400 group-hover:text-stone-700 transition-colors" />
                                </svg>
                            </div>
                        </div>
                        <h2 className="font-serif text-[clamp(26px,3vw,38px)] leading-tight text-stone-900 mb-3 group-hover:text-stone-700 transition-colors">
                            {cs.client}
                        </h2>
                        <p className="text-sm text-stone-500 leading-relaxed font-light mb-6">{cs.challenge}</p>

                        {/* Results */}
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            {cs.results.map(({ metric, delta }) => (
                                <div key={metric} className="bg-stone-50 rounded-xl p-3">
                                    <div className={`font-serif text-xl font-medium ${a.stat} mb-0.5`}>{delta}</div>
                                    <div className="text-[10px] text-stone-400 leading-tight">{metric}</div>
                                </div>
                            ))}
                        </div>

                        {/* Quote */}
                        <blockquote className={`border-l-2 pl-4 ${a.quote}`}>
                            <p className="text-sm text-stone-600 italic font-light leading-relaxed mb-1">"{cs.quote}"</p>
                            <cite className="text-xs text-stone-400 not-italic">{cs.quoteName}</cite>
                        </blockquote>
                    </div>

                    <div className="flex items-center gap-4 mt-6 pt-6 border-t border-black/5">
                        <span className="text-xs text-stone-400">{cs.duration} engagement</span>
                        <span className="text-xs text-stone-300">·</span>
                        <span className="text-xs text-stone-400">Results in {cs.timeToResults}</span>
                    </div>
                </div>
            </a>
        </Reveal>
    );
}

// ─── REGULAR CARD ─────────────────────────────────────────────────────────────
function CaseStudyCard({ cs, index }) {
    const a = ACCENT[cs.accent];
    return (
        <Reveal delay={index * 80}>
            <a
                href={cs.href}
                className={`group relative flex flex-col bg-white rounded-2xl border border-black/8 overflow-hidden no-underline transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${a.border} h-full`}
            >
                {/* Image strip */}
                <div className={`relative ${IMAGE_BG[cs.image]} flex items-center justify-center h-40`}>
                    <div className="text-5xl">{IMAGE_ICON[cs.image]}</div>
                    <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1">
                        {cs.services.map((s) => (
                            <span key={s} className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${a.tag}`}>{s}</span>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col flex-1">
                    <p className="text-[10px] text-stone-400 mb-1">{cs.industry} · {cs.location}</p>
                    <h3 className="font-serif text-xl text-stone-900 mb-2 group-hover:text-stone-700 transition-colors leading-snug">
                        {cs.client}
                    </h3>
                    <p className="text-xs text-stone-500 leading-relaxed font-light mb-5 flex-1">{cs.challenge}</p>

                    {/* Results mini-grid */}
                    <div className="grid grid-cols-3 gap-2 mb-5">
                        {cs.results.map(({ metric, delta }) => (
                            <div key={metric} className="bg-stone-50 rounded-lg p-2.5 text-center">
                                <div className={`font-serif text-base font-medium ${a.stat}`}>{delta}</div>
                                <div className="text-[9px] text-stone-400 leading-tight mt-0.5">{metric}</div>
                            </div>
                        ))}
                    </div>

                    {/* Quote */}
                    <blockquote className={`border-l-2 pl-3 mb-5 ${a.quote}`}>
                        <p className="text-xs text-stone-500 italic font-light leading-relaxed">"{cs.quote}"</p>
                        <cite className="text-[10px] text-stone-400 not-italic">{cs.quoteName}</cite>
                    </blockquote>

                    <div className="flex items-center justify-between pt-4 border-t border-black/5">
                        <span className="text-[10px] text-stone-400">{cs.duration} · {cs.timeToResults} to results</span>
                        <div className="w-6 h-6 rounded-full border border-black/10 flex items-center justify-center transition-all group-hover:border-black/30 group-hover:bg-stone-50">
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400 group-hover:text-stone-700 transition-colors" />
                            </svg>
                        </div>
                    </div>
                </div>
            </a>
        </Reveal>
    );
}

// ─── CTA BANNER ───────────────────────────────────────────────────────────────
function CtaBanner() {
    return (
        <Reveal>
            <div className="relative bg-stone-900 rounded-3xl px-10 py-14 text-center overflow-hidden mt-16">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-amber-400/10 rounded-full blur-3xl" />
                </div>
                <p className="text-xs font-medium tracking-widest uppercase text-amber-400 mb-4 relative">Your story next?</p>
                <h3 className="font-serif text-[clamp(28px,4vw,50px)] text-white leading-tight mb-4 relative max-w-xl mx-auto">
                    Every case study started<br />
                    <em className="italic text-amber-400">with one conversation.</em>
                </h3>
                <p className="text-sm text-white/50 max-w-md mx-auto font-light mb-8 relative leading-relaxed">
                    Whether you need a one-time audit or a full retainer, we'll tell you honestly what will move the needle — and what won't.
                </p>
                <div className="flex gap-3 justify-center flex-wrap relative">
                    <a href="/services/technical-seo-audit" className="bg-white text-stone-900 text-sm font-medium px-7 py-3 rounded-full no-underline hover:bg-amber-50 transition-colors duration-200">
                        Start with the audit — $750
                    </a>
                    <a href="/pricing" className="bg-transparent text-white text-sm font-normal px-7 py-3 rounded-full border border-white/20 no-underline hover:border-white/50 transition-colors duration-200">
                        View all retainers
                    </a>
                </div>
            </div>
        </Reveal>
    );
}

// ─── SUBMIT TEASER ────────────────────────────────────────────────────────────
function SubmitTeaser() {
    return (
        <Reveal>
            <div className="bg-amber-50 border border-amber-100 rounded-2xl px-8 py-7 flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                <div>
                    <p className="text-sm font-medium text-amber-900 mb-0.5">Are you a current client?</p>
                    <p className="text-xs text-amber-700/70 font-light">Share your results and we'll feature your story here.</p>
                </div>
                <a
                    href="mailto:hello@herstoryseo.com?subject=Case Study Feature"
                    className="shrink-0 text-xs font-medium bg-amber-800 text-white px-5 py-2.5 rounded-full no-underline hover:bg-amber-900 transition-colors duration-200"
                >
                    Submit your story →
                </a>
            </div>
        </Reveal>
    );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer() {
    return (
        <FooterView />
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function CaseStudiesHubPage() {
    const [activeFilter, setActiveFilter] = useState("all");

    const featured = CASE_STUDIES.find((cs) => cs.featured);
    const rest = CASE_STUDIES.filter((cs) => !cs.featured);

    const filteredFeatured =
        activeFilter === "all" || featured?.category === activeFilter ? featured : null;

    const filteredRest = rest.filter(
        (cs) => activeFilter === "all" || cs.category === activeFilter
    );

    return (
        <div className="font-sans bg-[#faf9f6] text-stone-900 min-h-screen">
            <Nav />
            <HeroSection />
            <FilterBar active={activeFilter} onChange={setActiveFilter} />

            <main className="max-w-6xl mx-auto px-6 pb-16">
                {/* Featured case study */}
                {filteredFeatured && <FeaturedCard cs={filteredFeatured} />}

                {/* Empty state */}
                {!filteredFeatured && filteredRest.length === 0 && (
                    <div className="text-center py-20 text-stone-400">
                        <p className="font-serif text-2xl mb-2">No case studies yet</p>
                        <p className="text-sm font-light">in this category — check back soon.</p>
                    </div>
                )}

                {/* Grid of remaining cards */}
                {filteredRest.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredRest.map((cs, i) => (
                            <CaseStudyCard key={cs.slug} cs={cs} index={i} />
                        ))}
                    </div>
                )}

                <SubmitTeaser />
                <CtaBanner />
            </main>

            <Footer />
        </div>
    );
}